import React from 'react';
import { useQuery } from '@apollo/client';
import { useDocumentState } from '../../../../../contexts/DocumentContext';
import LoadingSpinner from '../../../../loading-spinner';
import { useDocumentRole } from '../../../../../shared/hooks/useDocumentRole/useDocumentRole';
import { DocumentPermissions } from '../../../../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import ClauseTemplatesSidebar from './clauseTemplatesSidebar';
import { DocumentStatusEnum } from '../../../../../shared/constants/document';
import {
  getDocumentTemplates,
  getOrganizationsTemplatesByUser,
  getWorkspaceSharedTemplatesByUser,
} from '../../../../../api/queries_v2';
import { TemplateStatus } from '../../../../../pages/templates/templateConstants';
import { TemplateType } from '../../../../../consts/forms-types';
import { DocumentTemplateModalType } from '../../../../../pages/deals/document-template-modal/DocumentTemplateModalContainer';
import { classifyClauseBody, type ClauseBodyFormat } from '../../docx-native/clauseBody';

export interface ClauseTemplate {
  id: string;
  /** Raw stored body: base64-DOCX (`format: 'docx'`) or HTML (`format: 'html'`). */
  body: string;
  /** Which representation `body` holds. Docx clauses insert as .docx fragments. */
  format: ClauseBodyFormat;
  title: string;
  templateType?: string;
  status?: string;
}

interface ClauseTemplateProps {
  permissions: DocumentPermissions;
}

const ClauseTemplateContainer = ({ permissions }: ClauseTemplateProps) => {
  const { state: { id: documentId, teamId, status: documentStatus, isEditorDisabled, template } } = useDocumentState();

  // Check if we're in template editing mode vs document editing mode
  const isTemplateMode = !!template;

  // Only call useDocumentRole for actual documents, not templates
  const { isDocumentCreator, isDocumentRoleLoading } = useDocumentRole(isTemplateMode ? null : documentId);
  const { data: templatesData, loading: isTemplatesDataLoading } = useQuery(getDocumentTemplates, {
    variables: { teamId, templateType: 'clause', includeContent: true },
    skip: !documentId,
    fetchPolicy: 'network-only',
  });

  const { loading: organizationsTemplatesDataLoading, data: organizationsTemplatesData } = useQuery(
    getOrganizationsTemplatesByUser,
    {
      fetchPolicy: 'no-cache',
      variables: {
        teamId,
        templateType: 'clause',
        includeContent: true,
      },
    }
  );

  const { data: workspaceSharedTemplatesData } = useQuery(getWorkspaceSharedTemplatesByUser, {
    fetchPolicy: 'no-cache',
    variables: {
      teamId,
      templateType: 'clause',
      includeContent: true,
    },
  });

  const filterByTemplateType = (template: DocumentTemplateModalType | ClauseTemplate) => {
    return template?.templateType === TemplateType.CLAUSE && template?.status === TemplateStatus.ACTIVE;
  };

  const filterOrgsTemplates = (templates: [DocumentTemplateModalType]) => {
    return templates?.filter((template: DocumentTemplateModalType) => filterByTemplateType(template));
  };

  const relevantOrgsTemplates = filterOrgsTemplates(organizationsTemplatesData?.organizationsTemplatesByUser) || [];

  const relevantTeamTemplates =
    templatesData?.getDocumentTemplates.filter((template: any) => filterByTemplateType(template)) || [];

  const relevantWorkspaceSharedTemplates =
    workspaceSharedTemplatesData?.workspaceSharedTemplatesByUser.filter((template: any) =>
      filterByTemplateType(template)
    ) || [];

  const isDraftOrReviewing = [DocumentStatusEnum.DRAFT, DocumentStatusEnum.REVIEWING].includes(
    documentStatus as DocumentStatusEnum
  );

  // For templates, use different logic than for documents
  const isUpdatingDisabled = isTemplateMode
    ? !permissions.hasEditPermission // Template mode: only check permissions
    : isEditorDisabled || !isDocumentCreator || !isDraftOrReviewing || !permissions.hasEditPermission; // Document mode: check all conditions

  const allTemplates = [...relevantTeamTemplates, ...relevantOrgsTemplates, ...relevantWorkspaceSharedTemplates];
  const uniqueTemplates = allTemplates.filter(
    (template, index, self) => index === self.findIndex((t) => t.id === template.id)
  );
  // Decode a legacy HTML clause body that happens to be base64-encoded. DOCX
  // bodies are NOT decoded here — they stay as raw base64 and are inserted as
  // .docx fragments (decoding binary DOCX as text is what produced the old
  // garbled "encrypted" preview + broken drop).
  const decodeHtmlBody = (str: string | undefined): string => {
    if (!str) return '';
    try {
      if (/^[A-Za-z0-9+/]*={0,2}$/.test(str) && str.length > 20) {
        return decodeURIComponent(escape(atob(str)));
      }
      return str; // already plain HTML
    } catch {
      return str;
    }
  };

  const clauseTemplates: ClauseTemplate[] =
    uniqueTemplates.map((template: any) => {
      const raw = template.template.body as string | undefined;
      const format = classifyClauseBody(raw);
      return {
        // Docx: keep raw base64 (inserted as a fragment). Html: decode to markup.
        body: format === 'docx' ? (raw ?? '') : decodeHtmlBody(raw),
        format,
        title: template.name,
        id: template.id,
      };
    }) || [];

  const sortedTemplates = clauseTemplates.sort((a, b) => a.title.localeCompare(b.title));

  if (isDocumentRoleLoading || isTemplatesDataLoading || organizationsTemplatesDataLoading) {
    return <LoadingSpinner />;
  }

  // Refetch function to refresh clause list after creation
  const handleClauseCreated = () => {
    // Trigger a refetch of both queries to update the clause list
    if (teamId) {
      // The Apollo Client will automatically refetch due to the refetchQueries in the mutation
      // No additional action needed here since CreateClauseSlideout handles the refetch
    }
  };

  return (
    <ClauseTemplatesSidebar
      clauseTemplates={sortedTemplates}
      isDisabled={isUpdatingDisabled}
      teamId={teamId}
      onClauseCreated={handleClauseCreated}
      canManageTemplates={permissions?.hasManageTemplatesPermission}
    />
  );
};

export default ClauseTemplateContainer;
